import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PendingIncomeRow } from '@econtractor/types';
import PartialCountDialog from './PartialCountDialog';
import PendingIncomeActions from './PendingIncomeActions';
import PendingIncomeTable from './PendingIncomeTable';
import type { PendingWarehouseInfo } from './helper/canConfirmPending';
import usePendingIncome from './model/usePendingIncome';

interface PendingIncomeProps {
    wId: number;
    warehouseInfo: PendingWarehouseInfo | null;
    refreshVersion: number;
    onChanged: () => Promise<unknown> | unknown;
}

const PendingIncome = ({ wId, warehouseInfo, refreshVersion, onChanged }: PendingIncomeProps) => {
    const { t } = useTranslation(['Warehouses']);
    const [partialRow, setPartialRow] = useState<PendingIncomeRow | null>(null);
    const {
        rows,
        saving,
        canAct,
        selected,
        allSelected,
        toggle,
        toggleAll,
        acceptRows,
        rejectRows,
    } = usePendingIncome({ wId, warehouseInfo, refreshVersion, onChanged });

    if (!rows?.length) return null;

    return (
        <div className="mb-3">
            <h5 className="mb-2">{t('pendingIncome.title')}</h5>
            <PendingIncomeTable
                rows={rows}
                canAct={canAct}
                selected={selected}
                allSelected={allSelected}
                saving={saving}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onAcceptAll={(operationId) => acceptRows([{ operationId }])}
                onAcceptPartial={setPartialRow}
            />
            {canAct ? (
                <PendingIncomeActions
                    disabled={saving}
                    hasSelection={selected.length > 0}
                    onAccept={() => acceptRows(selected.map((operationId) => ({ operationId })))}
                    onReject={() => rejectRows(selected)}
                />
            ) : null}
            <PartialCountDialog
                total={partialRow ? Number(partialRow.operCount) : null}
                saving={saving}
                onClose={() => setPartialRow(null)}
                onSubmit={(count) => {
                    if (!partialRow) return;
                    acceptRows([{ operationId: partialRow.ID, count }]);
                    setPartialRow(null);
                }}
            />
        </div>
    );
};

export default PendingIncome;
